import { useEntrypointContext } from "../infrastructure/entrypoint/EntrypointContext";
import { useCallback, useEffect, useState } from "react";
import { EnkelVirksomhetInfo } from "../infrastructure/api/api";
import { findUriIfExists } from "../infrastructure/entrypoint/link";
import { getPossibleUndefinedJSON } from "../infrastructure/api/digipost-fetch";
import { useUnrecoverableError } from "../infrastructure/errors/useUnrecoverableError";

function matchesRegisteredRedirectUri(
  partnerOnboardingParamsRedirectUrl: string,
  integrasjonsPartnerRedirectUri: string,
): boolean {
  return (
    partnerOnboardingParamsRedirectUrl.startsWith(
      integrasjonsPartnerRedirectUri,
    ) ||
    integrasjonsPartnerRedirectUri.startsWith(
      partnerOnboardingParamsRedirectUrl,
    )
  );
}

export function useIntegrasjonsPartner() {
  const entrypoint = useEntrypointContext();
  const [integrasjonsPartner, setIntegrasjonsPartner] =
    useState<EnkelVirksomhetInfo>();
  const [integrasjonsPartnerResolved, setIntegrasjonsPartnerResolved] =
    useState<boolean>(false);
  const { partnerOnboardingParams } = useEntrypointContext();
  const { setCustomError, setCustomErrorWithDisplayMessage } =
    useUnrecoverableError();

  const validatePartnerOnboardingParams = useCallback(
    (integrasjonsPartner?: EnkelVirksomhetInfo) => {
      if (integrasjonsPartner === undefined) {
        setCustomErrorWithDisplayMessage("error.integration_partner_id");
        return false;
      }

      if (
        partnerOnboardingParams === undefined ||
        partnerOnboardingParams.partnerId === undefined ||
        partnerOnboardingParams.redirectUrl === undefined ||
        partnerOnboardingParams.nonce === undefined ||
        partnerOnboardingParams.organization === undefined
      ) {
        setCustomError(
          `PartnerOnboardingParams er ikke satt i entrypoint, eller mangler nødvendige parametere. PartnerOnboardingParams: ${JSON.stringify(partnerOnboardingParams)}`,
        );
        return false;
      }

      const integrasjonsPartnerRedirectUri = findUriIfExists(
        "INTEGRATION_PARTNER_REDIRECT_DOMAIN",
        integrasjonsPartner._links,
        true,
      );

      if (
        integrasjonsPartnerRedirectUri !== undefined &&
        matchesRegisteredRedirectUri(
          partnerOnboardingParams.redirectUrl,
          integrasjonsPartnerRedirectUri,
        )
      ) {
        return true;
      } else {
        setCustomErrorWithDisplayMessage(
          "error.integration_partner_redirect_uri_validation",
        );
        return false;
      }
    },
    [partnerOnboardingParams, setCustomError, setCustomErrorWithDisplayMessage],
  );

  useEffect(() => {
    const load = async () => {
      if (integrasjonsPartnerResolved) return;
      const uri = findUriIfExists("partneronboarding-info", entrypoint._links);
      if (uri) {
        const res = await getPossibleUndefinedJSON<EnkelVirksomhetInfo>(uri);
        if (validatePartnerOnboardingParams(res)) {
          setIntegrasjonsPartner(res);
        }
      }
      setIntegrasjonsPartnerResolved(true);
    };

    void load();
  }, [
    entrypoint._links,
    integrasjonsPartnerResolved,
    validatePartnerOnboardingParams,
  ]);

  return { integrasjonsPartner, integrasjonsPartnerResolved };
}
